import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, RadioGroup,
    FormControlLabel, Radio, Tabs, Tab, List, ListItem,
    ListItemText, ListItemSecondaryAction,
    Chip, Divider, CircularProgress, Avatar,
    Collapse
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import RemoveIconButton from '../common/RemoveIconButton';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { AnimationTiming } from '../../constants/enums';

const ShareLedgerDialog = ({ open, onClose, ledgerId, ledgerName }) => {
    const { t } = useTranslation();
    const { api, currentUser } = useAuth();
    const { showToast } = useToast();
    const [tab, setTab] = useState(0);
    const [emails, setEmails] = useState('');
    const [role, setRole] = useState('editor');
    const [sharedUsers, setSharedUsers] = useState([]);
    const [removingMemberIds, setRemovingMemberIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const currentUserInList = sharedUsers.find(u => u.id === currentUser?.id);
    const isAdmin = currentUserInList?.role === 'admin';

    useEffect(() => {
        if (!isAdmin && tab === 0 && !loading && sharedUsers.length > 0) {
            setTab(1);
        }
    }, [isAdmin, tab, loading, sharedUsers.length]);

    const fetchSharedUsers = useCallback(async () => {
        if (!ledgerId) return;
        setLoading(true);
        try {
            const response = await api.get(`/ledger/ledgers/${ledgerId}/users`);
            setSharedUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch shared users', error);
        } finally {
            setLoading(false);
        }
    }, [api, ledgerId]);

    useEffect(() => {
        if (open) {
            fetchSharedUsers();
            setTab(0);
            setEmails('');
            setRole('editor');
            setRemovingMemberIds(new Set());
        }
    }, [open, fetchSharedUsers]);

    useEffect(() => {
        const handleMembersChange = (event) => {
            if (event.detail?.ledgerId === ledgerId) {
                fetchSharedUsers();
            }
        };

        window.addEventListener('ledgerMembersChanged', handleMembersChange);
        return () => {
            window.removeEventListener('ledgerMembersChanged', handleMembersChange);
        };
    }, [ledgerId, fetchSharedUsers]);

    const handleShare = async () => {
        if (!ledgerId) {
            showToast('Book ID is missing. Please close and try again.', 'error');
            return;
        }

        const emailList = emails.split(/[,;\s]+/).map(e => e.trim()).filter(e => e);
        if (emailList.length === 0) {
            showToast('Please enter at least one email', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const response = await api.post(`/ledger/ledgers/${ledgerId}/share`, { emails: emailList, role });
            const { sent = [], ignored = [] } = response.data;

            if (sent.length > 0 && ignored.length > 0) {
                showToast(`Invitations sent. Already shared/pending ignored: ${ignored.join(', ')}`, 'info');
            } else if (sent.length > 0) {
                showToast('Invitations sent successfully', 'success');
            } else if (ignored.length > 0) {
                showToast(`Already shared or has a pending invitation: ${ignored.join(', ')}`, 'info');
            } else {
                showToast('No invitations were sent.', 'warning');
            }

            setEmails('');
            fetchSharedUsers();
            setTab(1); // Switch to manage tab to see pending invites
        } catch (error) {
            console.error('Failed to share book', error);
            showToast('Failed to send invitations', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveUser = async (memberId) => {
        if (removingMemberIds.has(memberId)) return;
        setRemovingMemberIds(prev => new Set(prev).add(memberId));

        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/users/${memberId}`);

            // Dispatch custom event to refresh ledgers in dashboard
            window.dispatchEvent(new CustomEvent('ledgerChanged'));

            if (memberId === currentUser?.id) {
                showToast(`You are removed from "${ledgerName}"`, 'success');
                onClose();
            } else {
                setTimeout(() => {
                    setSharedUsers(prev => prev.filter(u => u.id !== memberId));
                    setRemovingMemberIds(prev => {
                        const next = new Set(prev);
                        next.delete(memberId);
                        return next;
                    });
                }, AnimationTiming.RECORD_REMOVE_MS);
            }
        } catch (error) {
            setRemovingMemberIds(prev => {
                const next = new Set(prev);
                next.delete(memberId);
                return next;
            });
            console.error('Failed to remove user', error);
            showToast('Failed to remove user', 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, bgcolor: 'background.default' } }}>
            <DialogTitle sx={{ fontWeight: 800, mt: 1, color: 'primary.main' }}>
                {t("Share")} {ledgerName}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {isAdmin && (
                    <Tabs 
                        value={tab} 
                        onChange={(e, v) => setTab(v)} 
                        variant="fullWidth"
                        sx={{
                            px: 2,
                            borderBottom: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                            '& .MuiTabs-indicator': {
                                bgcolor: 'primary.main',
                                height: 3,
                                borderRadius: '3px 3px 0 0'
                            }
                        }}
                    >
                        <Tab 
                            label={t("Invite")} 
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: tab === 0 ? 'primary.main' : 'text.secondary',
                                opacity: tab === 0 ? 1 : 0.6,
                                '&&.Mui-selected': { color: 'primary.main' }
                            }} 
                        />
                        <Tab 
                            label={t("Manage")} 
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: tab === 1 ? 'primary.main' : 'text.secondary',
                                opacity: tab === 1 ? 1 : 0.6,
                                '&&.Mui-selected': { color: 'primary.main' }
                            }} 
                        />
                    </Tabs>
                )}
                <Box sx={{ p: 3 }}>
                    {(tab === 0 && isAdmin) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}>
                                    {t("Enter email addresses to invite collaborators to this book.")}
                                </Typography>
                                <TextField
                                    autoFocus
                                    multiline
                                    rows={3}
                                    fullWidth
                                    placeholder="e.g. hello@example.com"
                                    label={t("Email Addresses")}
                                    value={emails}
                                    onChange={(e) => setEmails(e.target.value)}
                                    variant="outlined"
                                    disabled={submitting}
                                    sx={{ 
                                        bgcolor: '#fff', 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: (theme) => alpha(theme.palette.secondary.main, 0.2) },
                                            '&:hover fieldset': { borderColor: (theme) => alpha(theme.palette.secondary.main, 0.4) },
                                            '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        },
                                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' }
                                    }}
                                />
                            </Box>
                            
                            <Box sx={{ p: 2, borderRadius: 3, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05), border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'primary.main' }}>{t("Access Permissions")}</Typography>
                                <RadioGroup row value={role} onChange={(e) => setRole(e.target.value)}>
                                    <FormControlLabel 
                                        value="editor" 
                                        control={<Radio size="small" sx={{ color: 'primary.main', '&.Mui-selected': { color: 'primary.main' } }} />} 
                                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{t("Editor")}</Typography>} 
                                    />
                                    <FormControlLabel 
                                        value="viewer" 
                                        control={<Radio size="small" sx={{ color: 'primary.main', '&.Mui-selected': { color: 'primary.main' } }} />} 
                                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{t("Viewer")}</Typography>} 
                                    />
                                </RadioGroup>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                    {role === 'editor' ? t('• Editors can add and modify entries.') : t('• Viewers have read-only access.')}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: 'primary.main' }} /></Box>
                            ) : sharedUsers.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
                                    <PersonIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
                                    <Typography variant="body2" sx={{ color: 'primary.main' }}>{t("No active collaborators yet.")}</Typography>
                                </Box>
                            ) : (
                                <List sx={{ width: '100%' }} disablePadding>
                                    {(() => {
                                        const currentUserInList = sharedUsers.find(u => u.id === currentUser?.id);
                                        const currentUserRole = currentUserInList?.role;

                                        return sharedUsers.map((user, idx) => {
                                            const isSelf = user.id === currentUser?.id;
                                            const isTargetAdmin = user.role === 'admin';
                                            const isCurrentUserAdmin = currentUserRole === 'admin';
                                            const showTrash = !isTargetAdmin && (isCurrentUserAdmin || isSelf);
                                            const isRemoving = removingMemberIds.has(user.id);

                                            return (
                                                <Collapse
                                                    key={user.id}
                                                    in={!isRemoving}
                                                    timeout={AnimationTiming.RECORD_REMOVE_MS}
                                                    unmountOnExit
                                                >
                                                    <ListItem 
                                                        sx={{ 
                                                            py: 2, 
                                                            px: 0,
                                                            transition: 'opacity 0.25s ease-out, transform 0.25s ease-out, background-color 0.25s ease-out',
                                                            opacity: isRemoving ? 0 : 1,
                                                            transform: isRemoving ? 'scale(0.96)' : 'none',
                                                            bgcolor: isRemoving ? 'action.hover' : 'transparent',
                                                            pointerEvents: isRemoving ? 'none' : 'auto'
                                                        }}
                                                    >
                                                        <Avatar sx={{ mr: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main', width: 40, height: 40 }}>
                                                            <PersonIcon fontSize="small" />
                                                        </Avatar>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                                        {user.first_name || user.email}
                                                                        {isSelf && ` (${t('You')})`}
                                                                    </Typography>
                                                                    {user.status === 'pending' && (
                                                                        <Chip 
                                                                            label={t("Pending")} 
                                                                            size="small" 
                                                                            sx={{ 
                                                                                height: 18, 
                                                                                fontSize: '0.6rem', 
                                                                                fontWeight: 700, 
                                                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                                                                color: 'primary.main',
                                                                                border: 'none',
                                                                                borderRadius: 1
                                                                            }} 
                                                                        />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                                    {user.email} • {t(user.role.charAt(0).toUpperCase() + user.role.slice(1))}
                                                                </Typography>
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            {showTrash && (
                                                                <RemoveIconButton
                                                                    edge="end"
                                                                    disabled={isRemoving}
                                                                    onClick={() => handleRemoveUser(user.id)}
                                                                    title={isSelf ? t("Remove yourself") : t("Remove user")}
                                                                />
                                                            )}
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                    {idx < sharedUsers.length - 1 && <Divider sx={{ opacity: 0.2 }} />}
                                                </Collapse>
                                            );
                                        });
                                    })()}
                                </List>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={onClose} sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'none', px: 2 }}>
                    {t("Close")}
                </Button>
                {tab === 0 && (
                    <Button
                        onClick={handleShare}
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
                        disabled={submitting || !emails.trim()}
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 700, 
                            textTransform: 'none',
                            px: 4,
                            boxShadow: 'none',
                        }}
                    >
                        {submitting ? t('Sharing...') : t('Send Invitations')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShareLedgerDialog;
